import { useState, useEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { getBgConfig, setBgConfig, BLUR_LEVELS, PRESET_BACKGROUNDS, type BgConfig } from '@/lib/virtualBackground';

/**
 * pre170: kleiner Pfeil neben dem Virtueller-Hintergrund-Button (analog Mic/Kamera).
 * Bietet eine schnelle Auswahl der verfügbaren Optionen: Aus, Weichzeichnen-Stufen
 * und die Preset-Hintergrundbilder. Auswahl wird sofort angewandt (setBgConfig) und
 * der aktive Zustand des Toolbar-Buttons über onChange gespiegelt.
 *
 * Öffnet nach OBEN (Toolbar unten) und per Portal an document.body.
 */
export function BackgroundMenu({ onChange }: { onChange: (active: boolean) => void }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; bottom: number } | null>(null);
  const [cur, setCur] = useState<BgConfig>(() => getBgConfig());
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    setCur(getBgConfig());
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ left: r.left + r.width / 2, bottom: window.innerHeight - r.top + 6 });
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  function apply(cfg: BgConfig) {
    setBgConfig(cfg);
    setCur(cfg);
    onChange(cfg.mode !== 'none');
    setOpen(false);
  }

  const isBlur = (px: number) => cur.mode === 'blur' && (cur.blur ?? 12) === px;
  const isImg = (src: string) => cur.mode === 'image' && cur.imageSrc === src;

  const sub: CSSProperties = { fontSize: 9, opacity: 0.45, padding: '8px 8px 3px', textTransform: 'uppercase', letterSpacing: 0.6 };
  const itemBase: CSSProperties = {
    display: 'block', width: '100%', textAlign: 'left', border: 'none', background: 'transparent',
    color: 'inherit', cursor: 'pointer', padding: '6px 8px', borderRadius: 5, fontSize: 12, fontFamily: 'inherit',
  };
  const Item = ({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) => (
    <button
      onClick={onClick}
      style={{ ...itemBase, fontWeight: active ? 700 : 400 }}
      onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.08)')}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
    >
      {active ? '● ' : '○ '}{children}
    </button>
  );

  return (
    <>
      <button
        ref={btnRef}
        title="Hintergrund wählen"
        onClick={() => setOpen((o) => !o)}
        style={{
          width: 16, height: 28, border: 'none', background: 'transparent', cursor: 'pointer',
          opacity: 0.9, padding: 0, marginLeft: -5,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && pos && createPortal(
        <div
          ref={menuRef}
          style={{
            position: 'fixed', left: pos.left, bottom: pos.bottom, transform: 'translateX(-50%)',
            width: 220, background: '#1c1c1a', color: '#f5f5f0',
            border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: 4,
            boxShadow: '0 8px 24px rgba(0,0,0,0.5)', zIndex: 1000, textAlign: 'left',
          }}
        >
          <div style={{ ...sub, paddingTop: 4 }}>Virtueller Hintergrund</div>
          <Item active={cur.mode === 'none'} onClick={() => apply({ mode: 'none' })}>Aus</Item>
          <div style={sub}>Weichzeichnen</div>
          {BLUR_LEVELS.map((b) => (
            <Item key={b.key} active={isBlur(b.px)} onClick={() => apply({ mode: 'blur', blur: b.px })}>{b.label}</Item>
          ))}
          <div style={sub}>Hintergrundbild</div>
          {PRESET_BACKGROUNDS.map((p) => (
            <Item key={p.id} active={isImg(p.src)} onClick={() => apply({ mode: 'image', imageSrc: p.src })}>{p.label}</Item>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
}
